import { useMemo } from "react";

import clsx from "clsx";
import { orderBy } from "lodash";

import { useUser } from "@components/user-context";
import { Spinner } from "@components/v2";
import { GET_UNIVERSAL_BENCHMARKS_FUNNEL_REPORT } from "@gql/universal-bentchmarks";
import { useNorthbeamQuery } from "@hooks/http";
import {
  type GetUniversalBenchmarksFunnelReport,
  type GetUniversalBenchmarksFunnelReportVariables,
} from "@north-beam/nb-client-side";

import { useUniversalBenchmarksConfiguratorContext } from "../../hooks";

const COLORS = ["#71D5D5", "#004F2D", "#FFD247", "#F05F33"];

interface Props {
  funnel: "top" | "bottom";
  title: string;
  subtitle: string;
}

export const FunnelReport = ({ funnel, title, subtitle }: Props) => {
  const { user } = useUser();
  const { daysSelection, kpiAttributionWindow, kpiAccountingMode } =
    useUniversalBenchmarksConfiguratorContext();

  const attributionModel =
    funnel === "top" ? "first_touch" : "last_touch_non_direct";
  const isSupported = Object.keys(user.attributionMethodEnum.enum).includes(
    attributionModel,
  );

  const { data, loading } = useNorthbeamQuery<
    GetUniversalBenchmarksFunnelReport,
    GetUniversalBenchmarksFunnelReportVariables
  >(GET_UNIVERSAL_BENCHMARKS_FUNNEL_REPORT, {
    variables: {
      selectedDays: daysSelection
        .filter((e) => e.isExcluded || e.willBeExcluded)
        .map((e) => e.day),
      funnel,
      attributionWindow: kpiAttributionWindow,
      accountingMode: kpiAccountingMode,
    },
    skip: !isSupported,
  });

  const funnelData = useMemo(() => {
    if (!data) {
      return null;
    }

    const result = [];

    const rows = orderBy(
      data.me.universalBenchmarksFunnelReport,
      [(e) => e.txns],
      ["desc"],
    );

    const mainRows = rows.slice(0, 3);
    const restRows = rows.slice(3);
    const totalValue = rows.reduce((acc, e) => (acc += e.txns), 0);
    const restValue = restRows.reduce((acc, e) => (acc += e.txns), 0);

    for (const row of mainRows) {
      result.push({
        value: row.txns,
        percent: row.txns / totalValue,
        label: row.platform,
      });
    }

    result.push({
      value: restValue,
      percent: restValue / totalValue,
      label: "Rest",
    });

    return result;
  }, [data]);

  let totalPercent = 0;
  const pieChartStyles = `conic-gradient(
    ${(funnelData || [])
      .map((row, index) => {
        const gradient = `${COLORS[index]} ${totalPercent * 100}% ${
          (totalPercent + row.percent) * 100
        }%`;
        totalPercent += row.percent;
        return gradient;
      })
      .join(", ")}
  )`;

  return (
    <div
      className={clsx(
        "border border-v2-tertiary rounded-lg p-4 min-h-[265px] flex flex-col justify-between",
      )}
    >
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <Spinner className="text-v2-cosmic/400" />
        </div>
      ) : (
        <>
          <div>
            <h3 className="text-base font-bold mb-0">{title}</h3>
            <p className="text-sm text-v2-secondary m-0">{subtitle}</p>
          </div>
          {funnelData ? (
            <>
              <div className="flex items-center justify-center">
                <div
                  className={clsx(
                    "rounded-full w-[140px] h-[140px] border-4 border-v2-background",
                    "shadow-[0_0_30px] shadow-v2-black/5%",
                  )}
                  style={{ backgroundImage: pieChartStyles }}
                ></div>
              </div>
              <div
                className={clsx(
                  "shrink-0 flex items-center justify-center gap-3 text-2xs",
                  "text-v2-secondary",
                )}
              >
                {funnelData.map((row, index) => {
                  return (
                    <div key={row.label} className="flex items-center gap-1.5">
                      <span
                        className={clsx("inline-block w-2 h-2 rounded-full")}
                        style={{ backgroundColor: COLORS[index] }}
                      ></span>
                      {row.label}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              <div className="flex-1 flex items-center justify-center text-xs">
                <div>
                  To view this report enable{" "}
                  <strong>
                    {user.attributionMethodEnum.getLabel(attributionModel)}
                  </strong>{" "}
                  attribution model.
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};
